import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useI18n } from '../context/I18nContext';
import { PlusIcon, SignalIcon, XIcon, CheckIcon, RefreshIcon, AlertIcon, TrashIcon } from '../components/Icons';

const WhatsAppBizIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/>
  </svg>
);

// Conexoes sao individuais por plataforma - o rotulo tem que refletir a que a
// conexao realmente e. Antes, tudo que nao fosse 'facebook' aparecia como
// 'Instagram', entao uma conexao WhatsApp se exibia como Instagram no painel.
const ROTULO_PLATAFORMA = {
  whatsapp: 'WhatsApp Business',
  instagram: 'Instagram',
  facebook: 'Facebook Messenger'
};

// Campo somente leitura com botao de copiar. Estas URLs existem para serem
// coladas no painel da Meta, e a da Meta exige match exato - digitar na mao e
// convite para um erro que so aparece la na frente, como redirect_uri
// invalido no meio do OAuth.
const CampoCopiavel = ({ rotulo, valor, dica }) => {
  const { t } = useI18n();
  const [copiado, setCopiado] = React.useState(false);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(valor);
    } catch (e) {
      // clipboard exige contexto seguro (https); em http o fallback e selecionar
      const campo = document.createElement('textarea');
      campo.value = valor;
      document.body.appendChild(campo);
      campo.select();
      document.execCommand('copy');
      document.body.removeChild(campo);
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 1800);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-900 mb-2">{rotulo}</label>
      <div className="flex gap-2">
        <input type="text" value={valor} readOnly
          className="flex-1 h-12 px-4 bg-gray-50 rounded-lg border border-gray-200 font-mono text-sm text-gray-500" />
        <button type="button" onClick={copiar}
          className={`px-4 h-12 rounded-lg text-sm font-medium border transition-colors ${
            copiado
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}>
          {copiado ? t('mc.copied') : t('mc.copy')}
        </button>
      </div>
      {dica && <p className="mt-1 text-xs text-gray-400">{dica}</p>}
    </div>
  );
};

const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2c2.717 0 3.056.01 4.122.06 1.065.05 1.79.217 2.428.465.66.254 1.216.598 1.772 1.153a4.908 4.908 0 0 1 1.153 1.772c.247.637.415 1.363.465 2.428.047 1.066.06 1.405.06 4.122 0 2.717-.01 3.056-.06 4.122-.05 1.065-.218 1.79-.465 2.428a4.883 4.883 0 0 1-1.153 1.772 4.915 4.915 0 0 1-1.772 1.153c-.637.247-1.363.415-2.428.465-1.066.047-1.405.06-4.122.06-2.717 0-3.056-.01-4.122-.06-1.065-.05-1.79-.218-2.428-.465a4.89 4.89 0 0 1-1.772-1.153 4.904 4.904 0 0 1-1.153-1.772c-.248-.637-.415-1.363-.465-2.428C2.013 15.056 2 14.717 2 12c0-2.717.01-3.056.06-4.122.05-1.066.217-1.79.465-2.428a4.88 4.88 0 0 1 1.153-1.772A4.897 4.897 0 0 1 5.45 2.525c.638-.248 1.362-.415 2.428-.465C8.944 2.013 9.283 2 12 2zm0 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm5.25-3.5a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5z"/>
  </svg>
);

const MetaConnections = () => {
  const { t } = useI18n();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);
  const [saving, setSaving] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '', connId: null });
  const [editData, setEditData] = useState({});
  const [logs, setLogs] = useState([]);
  const [sendForms, setSendForms] = useState({});
  const [sending, setSending] = useState(null);
  // Human Agent por conexao: a tag e decisao de cada mensagem.
  const [comoHumano, setComoHumano] = useState({});
  const [comments, setComments] = useState({});
  const [loadingComments, setLoadingComments] = useState(null);
  const [replyForms, setReplyForms] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  const [inbox, setInbox] = useState({});
  const [loadingInbox, setLoadingInbox] = useState(null);
  const [inboxReply, setInboxReply] = useState({});
  const [sendingInbox, setSendingInbox] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    platform: 'whatsapp',
    appId: '',
    appSecret: ''
  });

  const addLog = (type, text, details = null) => {
    const newLog = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toLocaleTimeString('pt-BR'),
      type, text, details
    };
    setLogs(prev => [newLog, ...prev].slice(0, 50));
  };

  const updateSendForm = (connId, field, value) => {
    setSendForms(prev => ({ ...prev, [connId]: { ...(prev[connId] || {}), [field]: value } }));
  };

  const handleSendMessage = async (conn) => {
    const form = sendForms[conn.id] || {};
    const recipient = (form.recipient || '').trim();
    const message = (form.message || '').trim();
    if (!recipient || !message) {
      addLog('warning', t('mc.fillRecipient'));
      return;
    }
    setSending(conn.id);
    addLog('info', t('metaconnec.a1', { v1: recipient, v2: message }));
    try {
      const res = await api.post(`/meta-connections/${conn.id}/send-message`,
        { recipient, message, comoHumano: !!comoHumano[conn.id] });
      const r = res.data && (res.data.result || res.data);
      addLog('success', t('metaconnec.a2', { v1: typeof r === 'object' ? JSON.stringify(r) : r }));
      updateSendForm(conn.id, 'message', '');
    } catch (err) {
      const e = err.response?.data?.error;
      const msg = typeof e === 'object' ? (e.message || JSON.stringify(e)) : (e || err.message);
      addLog('error', t('metaconnec.a3', { v1: msg }));
    } finally {
      setSending(null);
    }
  };

  const loadComments = async (conn) => {
    setLoadingComments(conn.id);
    addLog('info', t('metaconnec.a4'));
    try {
      const res = await api.get(`/meta-connections/${conn.id}/comments`);
      setComments(prev => ({ ...prev, [conn.id]: res.data.comentarios || [] }));
      addLog('success', t('metaconnec.a5', { v1: res.data.total }));
    } catch (err) {
      const e = err.response?.data?.error;
      addLog('error', t('metaconnec.a6', { v1: typeof e === 'object' ? (e.message || JSON.stringify(e)) : (e || err.message) }));
    } finally {
      setLoadingComments(null);
    }
  };

  const handleReplyComment = async (conn, comment) => {
    const text = (replyForms[comment.commentId] || '').trim();
    if (!text) { addLog('warning', t('mc.writeReply')); return; }
    setReplyingTo(comment.commentId);
    addLog('info', t('metaconnec.a7', { v1: comment.username, v2: text }));
    try {
      await api.post(`/meta-connections/${conn.id}/comments/${comment.commentId}/reply`, { message: text });
      addLog('success', t('metaconnec.a8', { v1: comment.username }));
      setReplyForms(prev => ({ ...prev, [comment.commentId]: '' }));
      loadComments(conn);
    } catch (err) {
      const e = err.response?.data?.error;
      addLog('error', t('metaconnec.a9', { v1: typeof e === 'object' ? (e.message || JSON.stringify(e)) : (e || err.message) }));
    } finally {
      setReplyingTo(null);
    }
  };

  const loadInbox = async (conn) => {
    setLoadingInbox(conn.id);
    addLog('info', t('mc.loadingChats'));
    try {
      const res = await api.get(`/meta-connections/${conn.id}/inbox`);
      setInbox(prev => ({ ...prev, [conn.id]: res.data.conversations || [] }));
      addLog('success', t('metaconnec.a10', { v1: res.data.total }));
    } catch (err) {
      const e = err.response?.data?.error;
      addLog('error', t('metaconnec.a11', { v1: typeof e === 'object' ? (e.message || JSON.stringify(e)) : (e || err.message) }));
    } finally {
      setLoadingInbox(null);
    }
  };

  const handleInboxReply = async (conn, cv) => {
    const text = (inboxReply[cv.convId] || '').trim();
    if (!text) { addLog('warning', t('mc.writeReply')); return; }
    setSendingInbox(cv.convId);
    try {
      await api.post(`/meta-connections/${conn.id}/send-message`,
        { recipient: cv.recipientId, message: text, comoHumano: !!comoHumano[conn.id] });
      addLog('success', t('metaconnec.a12', { v1: cv.recipientName }));
      setInboxReply(prev => ({ ...prev, [cv.convId]: '' }));
      loadInbox(conn);
    } catch (err) {
      const e = err.response?.data?.error;
      addLog('error', t('metaconnec.a13', { v1: typeof e === 'object' ? (e.message || JSON.stringify(e)) : (e || err.message) }));
    } finally {
      setSendingInbox(null);
    }
  };

  useEffect(() => {
    loadData();
    // Check URL params for OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('success')) {
      addLog('success', t('metaconnec.a14', { v1: params.get('success').toUpperCase() }));
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('error')) {
      // O backend devolve um codigo (login_cancelled, no_pages...). Traduzimos
      // quando conhecemos; caso contrario mostramos o codigo cru, que ainda e
      // melhor do que esconder a causa.
      const codigo = params.get('error');
      const traduzido = t('err.' + codigo);
      const texto = traduzido === 'err.' + codigo ? codigo : traduzido;
      addLog('error', t('metaconnec.a15', { v1: texto }));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadData = async () => {
    try {
      addLog('info', t('log.sepLoadMeta'));
      const response = await api.get('/meta-connections');
      setConnections(response.data);
      addLog('success', t('metaconnec.a16', { v1: response.data.length }));

      response.data.forEach(conn => {
        addLog('info', '---------------------------------------');
        addLog('info', t('metaconnec.a17', { v1: conn.name, v2: conn.platform }));
        addLog('info', `App ID: ${conn.appId}`);
        if (conn.pageId) addLog('info', `Page ID: ${conn.pageId}`);
        if (conn.instagramId) addLog('info', `Instagram ID: ${conn.instagramId}`);
        if (conn.pageName) addLog('info', t('metaconnec.a18', { v1: conn.pageName }));
        addLog(conn.status === 'connected' ? 'success' : 'warning', t('metaconnec.a19', { v1: conn.status.toUpperCase() }));
      });
      addLog('info', '=======================================');
    } catch (error) {
      addLog('error', t('metaconnec.a20', { v1: error.message }));
    }
    setLoading(false);
  };

  const handleTest = async (id) => {
    setTesting(id);
    const conn = connections.find(c => c.id === id);
    addLog('info', t('log.sepTesting', { v1: conn?.name }));
    try {
      const response = await api.post(`/meta-connections/${id}/test`);
      if (response.data.connected) {
        addLog('success', t('mc.statusConnected'));
        if (response.data.appName) addLog('success', `App: ${response.data.appName}`);
        if (response.data.pageName) addLog('success', t('metaconnec.a21', { v1: response.data.pageName }));
        if (response.data.igUsername) addLog('success', `Instagram: @${response.data.igUsername}`);
      } else {
        addLog('error', t('metaconnec.a22', { v1: response.data.success ? t('mc.appOkPageNot') : t('mc.errorUpper') }));
        if (response.data.error) addLog('error', t('metaconnec.a23', { v1: response.data.error }));
        if (response.data.pageError) addLog('error', `Page Error: ${response.data.pageError}`);
      }
      addLog('info', '=======================================');
      loadData();
    } catch (error) {
      addLog('error', t('metaconnec.a24', { v1: error.message }));
    }
    setTesting(null);
  };

  const handleConnect = async (id) => {
    setConnecting(id);
    const conn = connections.find(c => c.id === id);
    addLog('info', t('metaconnec.a25', { v1: conn?.name }));
    try {
      const response = await api.get(`/meta-connections/${id}/auth-url?platform=${conn?.platform || 'instagram'}`);
      addLog('success', t('mc.urlGenerated'));
      window.location.href = response.data.url;
    } catch (error) {
      addLog('error', t('metaconnec.a26', { v1: error.message }));
      setConnecting(null);
    }
  };

  const handleSave = async (conn) => {
    const data = editData[conn.id] || {};
    if (!Object.keys(data).length) {
      setSaveMessage({ type: 'info', text: t('mc.noChanges'), connId: conn.id });
      setTimeout(() => setSaveMessage({ type: '', text: '', connId: null }), 3000);
      return;
    }
    setSaving(conn.id);
    try {
      await api.put(`/meta-connections/${conn.id}`, data);
      addLog('success', t('metaconnec.a27', { v1: conn.name }));
      setSaveMessage({ type: 'success', text: t('mc.saved'), connId: conn.id });
      setEditData(prev => { const n = { ...prev }; delete n[conn.id]; return n; });
      loadData();
    } catch (error) {
      addLog('error', t('metaconnec.a28', { v1: error.message }));
      setSaveMessage({ type: 'error', text: error.response?.data?.error || t('mc.error'), connId: conn.id });
    }
    setSaving(null);
    setTimeout(() => setSaveMessage({ type: '', text: '', connId: null }), 5000);
  };

  const handleDelete = async (conn) => {
    if (!window.confirm(t('metaconnec.a29', { v1: conn.name }))) return;
    setDeleting(conn.id);
    try {
      await api.delete(`/meta-connections/${conn.id}`);
      addLog('success', t('metaconnec.a30', { v1: conn.name }));
      loadData();
    } catch (error) {
      addLog('error', t('metaconnec.a31', { v1: error.message }));
    }
    setDeleting(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    addLog('info', t('metaconnec.a32', { v1: formData.name }));
    try {
      const response = await api.post('/meta-connections', formData);
      addLog('success', t('mc.created'));
      if (response.data.testResult?.appName) addLog('success', `App: ${response.data.testResult.appName}`);
      addLog('info', `Webhook URL: ${response.data.webhookUrl}`);
      addLog('info', t('metaconnec.a33', { v1: response.data.verifyToken }));
      setShowModal(false);
      setFormData({ name: '', platform: 'instagram', appId: '', appSecret: '' });
      loadData();
    } catch (error) {
      addLog('error', t('metaconnec.a34', { v1: error.response?.data?.error || error.message }));
    }
  };

  const handleEditChange = (connId, field, value) => {
    setEditData(prev => ({ ...prev, [connId]: { ...prev[connId], [field]: value } }));
  };

  const PlatformIcon = ({ platform, className }) => {
    if (platform === 'facebook') return <FacebookIcon className={className} />;
    if (platform === 'whatsapp') return <WhatsAppBizIcon className={className} />;
    return <InstagramIcon className={className} />;
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('meta.title')}</h1>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-5 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20">
          <PlusIcon className="w-5 h-5" />{t('mc.newConnection')}</button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <div className="xl:col-span-3">
          {loading ? (
            <div className="text-center py-12 text-gray-500">
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span>{t('common.loading')}</span>
              </div>
            </div>
          ) : connections.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-100">
              <SignalIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 mb-4">{t('meta.none')}</p>
              <button onClick={() => setShowModal(true)}
                className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all">{t('mc.createFirst')}</button>
            </div>
          ) : (
            <div className="space-y-6">
              {connections.map(conn => (
                <div key={conn.id} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                        conn.platform === 'facebook' ? 'bg-blue-100' :
                        conn.platform === 'whatsapp' ? 'bg-green-100' :
                        'bg-gradient-to-br from-purple-100 to-pink-100'
                      }`}>
                        <PlatformIcon platform={conn.platform} className={`w-7 h-7 ${
                          conn.platform === 'facebook' ? 'text-blue-600' :
                          conn.platform === 'whatsapp' ? 'text-green-600' :
                          'text-pink-600'
                        }`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{conn.name}</h3>
                        <p className="text-sm text-gray-500">
                          {conn.platform === 'facebook' ? 'Facebook Messenger' : conn.platform === 'whatsapp' ? 'WhatsApp Business' : 'Instagram Direct'}
                          {conn.pageName ? ` — ${conn.pageName}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                      conn.status === 'connected' ? 'bg-green-100' :
                      conn.status === 'pending' ? 'bg-yellow-100' : 'bg-red-100'
                    }`}>
                      <div className={`w-2 h-2 rounded-full ${
                        conn.status === 'connected' ? 'bg-green-500' :
                        conn.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                      }`}></div>
                      <span className={`text-sm font-medium ${
                        conn.status === 'connected' ? 'text-green-600' :
                        conn.status === 'pending' ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {conn.status === 'connected' ? t('mc.connected') :
                         conn.status === 'pending' ? t('mc.pending') : t('mc.disconnected')}
                      </span>
                    </div>
                  </div>

                  <hr className="my-6 border-gray-100" />

                  {saveMessage.text && saveMessage.connId === conn.id && (
                    <div className={`mb-4 p-4 rounded-xl flex items-start gap-3 ${
                      saveMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                      saveMessage.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
                      'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                      <span className="text-sm">{saveMessage.text}</span>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">App ID</label>
                        <input type="text" value={conn.appId || ''} readOnly
                          className="w-full h-12 px-4 bg-gray-50 rounded-lg border border-gray-200 font-mono text-sm text-gray-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">{t('meta.platform')}</label>
                        <input type="text" value={ROTULO_PLATAFORMA[conn.platform] || conn.platform || 'Instagram'} readOnly
                          className="w-full h-12 px-4 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-500" />
                      </div>
                    </div>

                    {conn.pageId && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Page ID</label>
                          <input type="text" value={conn.pageId} readOnly
                            className="w-full h-12 px-4 bg-gray-50 rounded-lg border border-gray-200 font-mono text-sm text-gray-500" />
                        </div>
                        {conn.instagramId && (
                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-2">Instagram ID</label>
                            <input type="text" value={conn.instagramId} readOnly
                              className="w-full h-12 px-4 bg-gray-50 rounded-lg border border-gray-200 font-mono text-sm text-gray-500" />
                          </div>
                        )}
                      </div>
                    )}

                    {conn.verifyToken && (
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">{t('meta.verifyToken')}</label>
                        <input type="text" value={conn.verifyToken} readOnly
                          className="w-full h-12 px-4 bg-gray-50 rounded-lg border border-gray-200 font-mono text-sm text-gray-500" />
                        <p className="mt-1 text-xs text-gray-400">{t('meta.verifyTokenHint')}</p>
                      </div>
                    )}

                    {conn.webhookUrl && (
                      <CampoCopiavel
                        rotulo={t('mc.callbackUrl')}
                        valor={conn.webhookUrl}
                        dica={
                          conn.platform === 'instagram'
                            ? t('mc.hintIgWebhook')
                            : conn.platform === 'facebook'
                              ? t('mc.hintFbWebhook')
                              : 'Cadastre em WhatsApp > Configuracao > Webhook.'
                        }
                      />
                    )}

                    {conn.redirectUri && conn.platform !== 'whatsapp' && (
                      <CampoCopiavel
                        rotulo="Redirect URI (OAuth)"
                        valor={conn.redirectUri}
                        dica={
                          conn.platform === 'instagram'
                            ? t('metaconnec.a35')
                            : t('mc.hintFbRedirect')
                        }
                      />
                    )}

                    {conn.status === 'connected' && (conn.platform === 'instagram' || conn.platform === 'facebook') && (
                      <div className="border-t border-gray-100 pt-4 mt-2">
                        <label className="block text-sm font-semibold text-gray-900 mb-2">{t('meta.sendDirectTest')}</label>
                        <div className="space-y-2">
                          <input type="text" placeholder={conn.platform === 'facebook' ? t('mc.personName') : t('mc.igsidOrUser')}
                            value={(sendForms[conn.id] && sendForms[conn.id].recipient) || ''}
                            onChange={e => updateSendForm(conn.id, 'recipient', e.target.value)}
                            className="w-full h-12 px-4 bg-white rounded-lg border border-gray-200 text-sm" />
                          <textarea rows={2} placeholder={t('meta.messagePlaceholder')}
                            value={(sendForms[conn.id] && sendForms[conn.id].message) || ''}
                            onChange={e => updateSendForm(conn.id, 'message', e.target.value)}
                            className="w-full px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm" />
                          <label title={t('ha.hint')}
                            className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                            <input type="checkbox"
                              checked={!!comoHumano[conn.id]}
                              onChange={e => setComoHumano(prev => ({ ...prev, [conn.id]: e.target.checked }))}
                              className="w-4 h-4 accent-blue-600" />
                            {t('ha.label')}
                          </label>
                          <button onClick={() => handleSendMessage(conn)} disabled={sending === conn.id}
                            className="flex items-center gap-2 px-5 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors">
                            {sending === conn.id ? t('mc.sending') : t('mc.sendMessage')}
                          </button>
                        </div>
                        <p className="mt-1 text-xs text-gray-400">{t('meta.igsidHint')}</p>
                      </div>
                    )}

                    {conn.status === 'connected' && (conn.platform === 'instagram' || conn.platform === 'facebook') && (
                      <div className="border-t border-gray-100 pt-4 mt-2">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-semibold text-gray-900">{t('meta.conversations')}</label>
                          <button onClick={() => loadInbox(conn)} disabled={loadingInbox === conn.id}
                            className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50">
                            {loadingInbox === conn.id ? t('mc.loading') : t('mc.loadChats')}
                          </button>
                        </div>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {(inbox[conn.id] || []).length === 0 ? (
                            <p className="text-xs text-gray-400">{t('meta.noConversations')}</p>
                          ) : (inbox[conn.id] || []).map(cv => (
                            <div key={cv.convId} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                              <p className="text-sm font-semibold text-gray-900 mb-2">{cv.recipientName}</p>
                              <div className="space-y-1 mb-2">
                                {cv.messages.map((m, i) => (
                                  <div key={i} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
                                    <span className={`inline-block px-3 py-1.5 rounded-2xl text-sm max-w-xs ${m.mine ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
                                      {m.text || t('mc.noText')}
                                    </span>
                                  </div>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <input type="text" placeholder={t('meta.replyPlaceholder')}
                                  value={inboxReply[cv.convId] || ''}
                                  onChange={e => setInboxReply(prev => ({ ...prev, [cv.convId]: e.target.value }))}
                                  onKeyDown={e => { if (e.key === 'Enter') handleInboxReply(conn, cv); }}
                                  className="flex-1 h-9 px-3 bg-white rounded-lg border border-gray-200 text-sm" />
                                <button onClick={() => handleInboxReply(conn, cv)} disabled={sendingInbox === cv.convId}
                                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                  {sendingInbox === cv.convId ? '...' : t('mc.send')}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {conn.status === 'connected' && (conn.platform === 'instagram' || conn.platform === 'facebook') && (
                      <div className="border-t border-gray-100 pt-4 mt-2">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-semibold text-gray-900">{t('meta.comments')}</label>
                          <button onClick={() => loadComments(conn)} disabled={loadingComments === conn.id}
                            className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50">
                            {loadingComments === conn.id ? t('mc.loading') : t('mc.loadComments')}
                          </button>
                        </div>
                        <div className="space-y-3 max-h-80 overflow-y-auto">
                          {(comments[conn.id] || []).length === 0 ? (
                            <p className="text-xs text-gray-400">{t('meta.noComments')}</p>
                          ) : (comments[conn.id] || []).map(cm => (
                            <div key={cm.commentId} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                              <p className="text-sm text-gray-900"><span className="font-semibold">{cm.author}</span> <span className="text-xs text-gray-400">em "{cm.postCaption}"</span></p>
                              <p className="text-sm text-gray-700 mt-1">{cm.text}</p>
                              {cm.replies && cm.replies.length > 0 && (
                                <div className="mt-2 pl-3 border-l-2 border-gray-200 space-y-1">
                                  {cm.replies.map((rp, i) => <p key={i} className="text-xs text-gray-500"><span className="font-medium">{rp.author}:</span> {rp.text}</p>)}
                                </div>
                              )}
                              <div className="flex gap-2 mt-2">
                                <input type="text" placeholder={t('meta.replyCommentPlaceholder')}
                                  value={replyForms[cm.commentId] || ''}
                                  onChange={e => setReplyForms(prev => ({ ...prev, [cm.commentId]: e.target.value }))}
                                  className="flex-1 h-9 px-3 bg-white rounded-lg border border-gray-200 text-sm" />
                                <button onClick={() => handleReplyComment(conn, cm)} disabled={replyingTo === cm.commentId}
                                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50">
                                  {replyingTo === cm.commentId ? '...' : t('mc.reply')}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center mt-6">
                    <button onClick={() => handleDelete(conn)} disabled={deleting === conn.id}
                      className="flex items-center gap-2 px-5 py-3 bg-red-50 text-red-600 font-medium rounded-xl hover:bg-red-100 disabled:opacity-50 transition-colors">
                      <TrashIcon className="w-4 h-4" />
                      {deleting === conn.id ? t('mc.deleting') : t('mc.delete')}
                    </button>
                    <div className="flex gap-3">
                      <button onClick={() => handleTest(conn.id)} disabled={testing === conn.id}
                        className="flex items-center gap-2 px-5 py-3 bg-gray-100 text-gray-600 font-medium rounded-xl hover:bg-gray-200 disabled:opacity-50 transition-colors">
                        <RefreshIcon className={`w-4 h-4 ${testing === conn.id ? 'animate-spin' : ''}`} />
                        {testing === conn.id ? t('mc.testing') : t('mc.test')}
                      </button>
                      {/* Sempre visivel: conta ja conectada precisa reconectar para o
                          token receber escopos novos (publicar e metricas). */}
                      <button onClick={() => handleConnect(conn.id)} disabled={connecting === conn.id}
                        title={conn.status === 'connected' ? t('mc.reconnectHint') : undefined}
                        className={`flex items-center gap-2 px-5 py-3 font-semibold rounded-xl disabled:opacity-50 transition-all ${
                          conn.status === 'connected'
                            ? 'bg-white text-blue-700 border border-blue-200 hover:bg-blue-50'
                            : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/20'
                        }`}>
                        {connecting === conn.id ? (
                          <><div className={`w-4 h-4 border-2 border-t-transparent rounded-full animate-spin ${conn.status === 'connected' ? 'border-blue-700' : 'border-white'}`}></div>{t('meta.connecting')}</>
                        ) : (
                          <>
                            <PlatformIcon platform={conn.platform} className="w-4 h-4" />
                            {t(conn.status === 'connected' ? 'mc.reconnectPlatform' : 'mc.connectPlatform',
                               { v1: conn.platform === 'facebook' ? 'Facebook' : conn.platform === 'whatsapp' ? 'WhatsApp' : 'Instagram' })}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Logs */}
        <div className="xl:col-span-2">
          <div className="bg-gray-900 rounded-2xl shadow-sm border border-gray-800 sticky top-8 flex flex-col" style={{ maxHeight: 'calc(100vh - 120px)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                Logs Meta API
              </h3>
              <button onClick={() => setLogs([])} className="text-xs text-gray-400 hover:text-white transition-colors">{t('meta.clear')}</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 font-mono text-xs" style={{ maxHeight: '700px' }}>
              {logs.length === 0 ? (
                <div className="text-gray-500 text-center py-8">{t('meta.noLogs')}</div>
              ) : logs.map(log => (
                <div key={log.id} className="flex flex-col gap-1">
                  <div className="flex gap-2">
                    <span className="text-gray-500 flex-shrink-0">[{log.timestamp}]</span>
                    <span className={
                      log.type === 'success' ? 'text-green-400' :
                      log.type === 'error' ? 'text-red-400' :
                      log.type === 'warning' ? 'text-yellow-400' : 'text-blue-400'
                    }>
                      {log.type === 'success' && '+ '}{log.type === 'error' && 'x '}{log.type === 'warning' && '! '}{log.type === 'info' && '> '}
                      {log.text}
                    </span>
                  </div>
                  {log.details && (
                    <pre className="ml-4 mt-1 p-2 bg-gray-800 rounded text-gray-300 text-xs overflow-x-auto whitespace-pre-wrap">{log.details}</pre>
                  )}
                </div>
              ))}
            </div>
            <div className="px-4 py-2 border-t border-gray-800 text-xs text-gray-500">{logs.length} entradas</div>
          </div>
        </div>
      </div>

      {/* Modal Nova Conexao */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">{t('meta.newConnection')}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><XIcon className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t('meta.connectionName')}</label>
                <input type="text" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('meta.connectionNamePlaceholder')}
                  className="w-full h-12 px-4 bg-gray-50 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t('meta.platform')}</label>
                <select value={formData.platform}
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                  className="w-full h-12 px-4 bg-gray-50 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
                  <option value="whatsapp">{t('meta.platformWhatsapp')}</option>
                  <option value="instagram">Instagram</option>
                  <option value="facebook">{t('meta.platformFacebook')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t('meta.appIdRequired')}</label>
                <input type="text" value={formData.appId}
                  onChange={(e) => setFormData({ ...formData, appId: e.target.value })}
                  placeholder={t('meta.appIdHint')}
                  className="w-full h-12 px-4 bg-gray-50 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                  required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t('meta.appSecretRequired')}</label>
                <input type="password" value={formData.appSecret}
                  onChange={(e) => setFormData({ ...formData, appSecret: e.target.value })}
                  placeholder={t('meta.appSecretHint')}
                  className="w-full h-12 px-4 bg-gray-50 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                  required />
              </div>
              <p className="text-xs text-gray-500">{t('metaconnec.j1')}</p>
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-600 font-medium rounded-xl hover:bg-gray-200 transition-colors">{t('mc.cancel')}</button>
                <button type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20">{t('mc.createConnection')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetaConnections;
